import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DbAssetObjectInfos } from "../../Core/Api/Types/db-asset-object-infos";
import { RootState } from "../redux-store";

export enum DrivePanelState {
  Preview = "preview",
  Enlarged = "enlarged",
  Closed = "closed",
}

interface ReduxDriveState {
  tree: {
    currentObj: DbAssetObjectInfos | undefined;
    objectResults: DbAssetObjectInfos[];
  };
  panel: {
    panelState: DrivePanelState;
    panelObj: DbAssetObjectInfos | undefined;
  };
  search: {
    isSearch: boolean;
    searchResults: DbAssetObjectInfos[];
  };
  pagination: {
    totalChildrenCount: number;
    hasNextPage: boolean;
    loadedChildrenCount: number;
    isNextPageLoading: boolean;
  };
  loadingStates: {
    treeLoading: boolean;
    breadcrumbsLoading: boolean;
    folderLoading: boolean;
    panelLoading: boolean;
  };
  interface: {
    scales: number[];
    scale: number;
    layout: "list" | "grid";
    filters: {
      text: string;
    };
  };
}

const initialState: ReduxDriveState = {
  tree: {
    currentObj: undefined,
    objectResults: [],
  },
  panel: {
    panelState: DrivePanelState.Closed,
    panelObj: undefined,
  },
  search: {
    isSearch: false,
    searchResults: [],
  },
  pagination: {
    totalChildrenCount: 0,
    hasNextPage: false,
    loadedChildrenCount: 0,
    isNextPageLoading: false,
  },
  loadingStates: {
    treeLoading: false,
    breadcrumbsLoading: false,
    folderLoading: false,
    panelLoading: false,
  },
  interface: {
    scales: [2, 3, 4, 6, 12],
    scale: 2,
    layout: "list",
    filters: {
      text: "",
    },
  },
};

const driveSlice = createSlice({
  name: "Drive",
  initialState,
  reducers: {
    SetPanelState: (state, action: PayloadAction<DrivePanelState>) => {
      state.panel.panelState = action.payload;
    },
    SetScales: (state, action: PayloadAction<number[]>) => {
      state.interface.scales = action.payload;
    },
    SetActiveScale: (state, action: PayloadAction<number>) => {
      state.interface.scale = state.interface.scales.find((scale) => scale === action.payload) ?? state.interface.scale;
    },
    SetLayout: (state, action: PayloadAction<"list" | "grid">) => {
      state.interface.layout = action.payload;
    },
    ToggleEnlargePanel: (state) => {
      state.panel.panelState =
        state.panel.panelState === DrivePanelState.Enlarged ? DrivePanelState.Preview : DrivePanelState.Enlarged;
    },
    SetFilterText: (state, action: PayloadAction<string>) => {
      state.interface.filters.text = action.payload;
    },
    SetIsSearch: (state, action: PayloadAction<boolean>) => {
      state.search.isSearch = action.payload;
    },
    SetPagination: (
      state,
      action: PayloadAction<{
        isNextPageLoading?: boolean;
        totalChildrenCount?: number;
        hasNextPage?: boolean;
        loadedChildrenCount?: number;
      }>
    ) => {
      const hasNextPage = state.pagination.loadedChildrenCount < state.pagination.totalChildrenCount;
      state.pagination = {
        ...state.pagination,
        hasNextPage:
          (action.payload.loadedChildrenCount ?? state.pagination.loadedChildrenCount) <
          (action.payload.totalChildrenCount ?? state.pagination.totalChildrenCount),
        ...action.payload,
      };
    },
    SetCurrentObject: (state, action: PayloadAction<DbAssetObjectInfos>) => {
      return {
        ...state,
        tree: {
          ...state.tree,
          currentObj: action.payload,
        },
      };
    },
    ResetCurrentObject: (state) => {
      state.tree.currentObj = undefined;
    },
    SetPanelObject: (state, action: PayloadAction<DbAssetObjectInfos>) => {
      return {
        ...state,
        panel: {
          ...state.panel,
          panelObj: action.payload,
        },
      };
    },
    ResetPanelObject: (state) => {
      return {
        ...state,
        panel: {
          panelState: DrivePanelState.Closed,
          panelObj: undefined,
          panelDto: undefined,
        },
      };
    },
    SetSearchResults: (state, action: PayloadAction<DbAssetObjectInfos[]>) => {
      return {
        ...state,
        search: {
          ...state.search,
          searchResults: action.payload,
        },
      };
    },
    SetObjectResults: (state, action: PayloadAction<DbAssetObjectInfos[]>) => {
      return {
        ...state,
        tree: {
          ...state.tree,
          objectResults: action.payload,
        },
      };
    },
    SetLoadingStates: (
      state,
      action: PayloadAction<{
        treeLoading?: boolean;
        breadcrumbsLoading?: boolean;
        folderLoading?: boolean;
        panelLoading?: boolean;
      }>
    ) => {
      state.loadingStates = { ...state.loadingStates, ...action.payload };
    },
    RenameObj: (state, action: PayloadAction<{ objId: number; newName: string }>) => {
      let targetIndex = state.search.isSearch
        ? state.search.searchResults.findIndex((x) => x.id === action.payload.objId)
        : state.tree.objectResults.findIndex((x) => x.id === action.payload.objId);

      if (targetIndex === -1) return;

      if (state.search.isSearch) {
        state.search.searchResults[targetIndex].name = action.payload.newName;
      } else {
        state.tree.objectResults[targetIndex].name = action.payload.newName;
      }
    },
    RemoveObjs: (state, action: PayloadAction<number[]>) => {
      state.tree.objectResults = state.tree.objectResults.filter((obj) => !action.payload.includes(obj.id));
    },
    TrashObj: (state, action: PayloadAction<{ objId: number }>) => {
      const targetIndex = state.search.isSearch
        ? [...state.search.searchResults].findIndex((x) => x.id === action.payload.objId)
        : [...state.tree.objectResults].findIndex((x) => x.id === action.payload.objId);
      if (targetIndex === -1) return;

      if (state.search.isSearch) {
        state.search.searchResults = state.search.searchResults.filter((obj) => obj.id !== action.payload.objId);
      } else {
        state.tree.objectResults = state.tree.objectResults.filter((obj) => obj.id !== action.payload.objId);
      }
    },
    UntrashObj: (state, action: PayloadAction<{ objId: number }>) => {
      state.tree.objectResults = state.tree.objectResults.filter((obj) => obj.id !== action.payload.objId);
    },
    AddObjs: (state, action: PayloadAction<DbAssetObjectInfos[]>) => {
      state.tree.objectResults = [...state.tree.objectResults, ...action.payload];
    },
    ResetDriveData: (state) => {
      state = { ...initialState };
    },
  },
});

export const {
  SetCurrentObject,
  ResetCurrentObject,
  SetObjectResults,
  SetPanelState,
  SetPanelObject,
  ToggleEnlargePanel,
  ResetPanelObject,
  SetIsSearch,
  SetSearchResults,
  SetPagination,
  SetLayout,
  SetScales,
  SetActiveScale,
  SetFilterText,
  SetLoadingStates,
  RenameObj,
  RemoveObjs,
  TrashObj,
  UntrashObj,
  AddObjs,
  ResetDriveData,
} = driveSlice.actions;

export const GetPanelObject = (state: RootState): DbAssetObjectInfos | undefined =>
  state.driveSlice.panel.panelObj ?? undefined;
export const GetPanelState = (state: RootState): DrivePanelState => state.driveSlice.panel.panelState;

export const GetCurrentObj = (state: RootState): DbAssetObjectInfos | undefined =>
  state.driveSlice.tree.currentObj ?? undefined;
export const GetObjectResults = (state: RootState): DbAssetObjectInfos[] => state.driveSlice.tree.objectResults;

export const GetIsSearch = (state: RootState): boolean => state.driveSlice.search.isSearch;
export const GetSearchResults = (state: RootState): DbAssetObjectInfos[] => state.driveSlice.search.searchResults;

export const GetScale = (state: RootState): number => state.driveSlice.interface.scale;
export const GetScales = (state: RootState): number[] => state.driveSlice.interface.scales;
export const GetLayout = (state: RootState): "list" | "grid" => state.driveSlice.interface.layout;
export const GetFilterText = (state: RootState): string => state.driveSlice.interface.filters.text;

export const GetPagination = (
  state: RootState
): { totalChildrenCount: number; hasNextPage: boolean; loadedChildrenCount: number; isNextPageLoading: boolean } =>
  state.driveSlice.pagination;
export const GetLoadingStates = (
  state: RootState
): { treeLoading: boolean; breadcrumbsLoading: boolean; folderLoading: boolean; panelLoading: boolean } =>
  state.driveSlice.loadingStates;

export default driveSlice.reducer;
